import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { DollarSign, Plus, Minus, Search, TrendingUp, Wallet, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CreditManagement = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [action, setAction] = useState('add'); // 'add' or 'deduct'
  
  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/admin/credits/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast({
        title: isArabic ? 'خطأ' : 'Error',
        description: 'Failed to load users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [isArabic]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  const handleOpenDialog = (user, actionType) => {
    setSelectedUser(user);
    setAction(actionType);
    setCreditAmount('');
    setCreditNote('');
    setShowAddDialog(true);
  };
  
  const handleUpdateCredits = async () => {
    if (!creditAmount || parseFloat(creditAmount) <= 0) {
      toast({
        title: isArabic ? 'خطأ' : 'Error',
        description: isArabic ? 'الرجاء إدخال مبلغ صحيح' : 'Please enter a valid amount',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'add' ? '/api/admin/credits/add' : '/api/admin/credits/deduct';
      
      const response = await axios.post(
        `${API_BASE}${endpoint}`,
        {
          userId: selectedUser.userId,
          amount: parseFloat(creditAmount),
          action: action,  // Added action field
          note: creditNote || (action === 'add' ? 'Cash payment received' : 'Credits deducted')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast({
        title: isArabic ? 'نجح' : 'Success',
        description: response.data.message,
        className: 'bg-green-600 text-white'
      });
      
      setShowAddDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Credit update error:', error);
      const errorMessage = error.response?.data?.detail || 
                          (error.response?.data?.message) || 
                          'Failed to update credits';
      toast({
        title: isArabic ? 'خطأ' : 'Error',
        description: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        variant: 'destructive'
      });
    }
  };
  
  const filteredUsers = users.filter(user =>
    user.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.userEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const totalCredits = users.reduce((sum, user) => sum + user.balance, 0);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{isArabic ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">{isArabic ? 'إدارة الرصيد' : 'Credit Management'}</h2>
          <p className="text-gray-600 mt-1">
            {isArabic ? 'إدارة أرصدة المستخدمين والسائقين' : 'Manage user and driver credit balances'}
          </p>
        </div>
      </div>
      
      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          {isArabic 
            ? 'عندما يدفع المستخدم أو السائق نقداً في المكتب، أضف الرصيد هنا. سيتم خصم الرصيد تلقائياً عند حجز الرحلات.'
            : 'When users or drivers pay cash at the office, add credits here. Credits are automatically deducted when booking rides.'
          }
        </AlertDescription>
      </Alert>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm mb-1">{isArabic ? 'إجمالي الأرصدة' : 'Total Credits'}</p>
              <p className="text-3xl font-bold">{totalCredits.toLocaleString()}</p>
              <p className="text-green-100 text-xs mt-1">{isArabic ? 'ل.س' : 'SYP'}</p>
            </div>
            <Wallet className="h-12 w-12 text-green-200" />
          </div>
        </Card>
        
        <Card className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">{isArabic ? 'المستخدمين بأرصدة' : 'Users with Credits'}</p>
              <p className="text-3xl font-bold">{users.filter(u => u.balance > 0).length}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-200" />
          </div>
        </Card>
        
        <Card className="p-6 bg-gradient-to-br from-black to-black text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-black text-sm mb-1">{isArabic ? 'إجمالي المستخدمين' : 'Total Users'}</p>
              <p className="text-3xl font-bold">{users.length}</p>
            </div>
            <DollarSign className="h-12 w-12 text-black" />
          </div>
        </Card>
      </div>
      
      {/* Search */}
      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder={isArabic ? 'بحث عن مستخدم...' : 'Search user...'}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </Card>
      
      {/* Users List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredUsers.map((user) => (
          <Card key={user.userId} className="p-4 hover:shadow-lg transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-lg">{user.userName}</h3>
                <p className="text-sm text-gray-600">{user.userEmail}</p>
                <Badge className="mt-2" variant={user.userRole === 'driver' ? 'default' : 'secondary'}>
                  {user.userRole === 'driver' ? (isArabic ? 'سائق' : 'Driver') : (isArabic ? 'راكب' : 'Passenger')}
                </Badge>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-black to-black rounded-lg p-4 mb-3">
              <p className="text-sm text-gray-600 mb-1">{isArabic ? 'الرصيد الحالي' : 'Current Balance'}</p>
              <p className="text-2xl font-bold text-black">
                {user.balance.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => handleOpenDialog(user, 'add')}
              >
                <Plus className="mr-1 h-4 w-4" />
                {isArabic ? 'إضافة' : 'Add'}
              </Button>
              <Button 
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => handleOpenDialog(user, 'deduct')}
                disabled={user.balance <= 0}
              >
                <Minus className="mr-1 h-4 w-4" />
                {isArabic ? 'خصم' : 'Deduct'}
              </Button>
            </div>
          </Card>
        ))}
      </div>
      
      {filteredUsers.length === 0 && (
        <Card className="p-12 text-center">
          <p className="text-gray-500">{isArabic ? 'لم يتم العثور على مستخدمين' : 'No users found'}</p>
        </Card>
      )}
      
      {/* Add/Deduct Credits Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {action === 'add' 
                ? (isArabic ? 'إضافة رصيد' : 'Add Credits')
                : (isArabic ? 'خصم رصيد' : 'Deduct Credits')
              }
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 py-4">
              <Card className="p-4 bg-gray-50">
                <p className="font-semibold">{selectedUser.userName}</p>
                <p className="text-sm text-gray-600">{selectedUser.userEmail}</p>
                <p className="text-lg font-bold text-black mt-2">
                  {isArabic ? 'الرصيد الحالي: ' : 'Current Balance: '}
                  {selectedUser.balance.toLocaleString()} {isArabic ? 'ل.س' : 'SYP'}
                </p>
              </Card>
              
              <div>
                <Label htmlFor="amount">{isArabic ? 'المبلغ (ل.س)' : 'Amount (SYP)'}</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder={isArabic ? 'أدخل المبلغ' : 'Enter amount'}
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                />
              </div>
              
              <div>
                <Label htmlFor="note">{isArabic ? 'ملاحظة (اختياري)' : 'Note (Optional)'}</Label>
                <Input
                  id="note"
                  placeholder={isArabic ? 'أضف ملاحظة...' : 'Add a note...'}
                  value={creditNote}
                  onChange={(e) => setCreditNote(e.target.value)}
                />
              </div>
              
              {creditAmount && (
                <Alert className={action === 'add' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
                  <AlertDescription className={action === 'add' ? 'text-green-800' : 'text-red-800'}>
                    {isArabic ? 'الرصيد الجديد سيكون: ' : 'New balance will be: '}
                    <strong>
                      {action === 'add'
                        ? (selectedUser.balance + parseFloat(creditAmount || 0)).toLocaleString()
                        : (selectedUser.balance - parseFloat(creditAmount || 0)).toLocaleString()
                      } {isArabic ? 'ل.س' : 'SYP'}
                    </strong>
                  </AlertDescription>
                </Alert>
              )}
              
              <Button
                className={`w-full ${action === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                onClick={handleUpdateCredits}
              >
                {action === 'add'
                  ? (isArabic ? 'إضافة رصيد' : 'Add Credits')
                  : (isArabic ? 'خصم رصيد' : 'Deduct Credits')
                }
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditManagement;
